import { useAddCameraGroup, useLocations } from '@hakimo-ui/hakimo/data-access';
import { Floorplan } from '@hakimo-ui/hakimo/feature-shared';
import { CameraGroupPayload } from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraGroupHeader from './CameraGroupHeader';
import CameraGroupFormCard from './cards/CameraGroupFormCard';
import CameraGroupSelectorCard from './cards/CameraGroupSelectorCard';

interface Props {
  onClose: () => void;
}

function AddCameraGroup({ onClose }: Props) {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<CameraGroupPayload>({
    name: '',
    description: '',
    locationId: '',
    cameras: [],
  });

  const addGroupMutation = useAddCameraGroup((data) => {
    toast(`Camera group ${payload.name} added successfully!`, {
      type: 'success',
    });
    navigate(`/cameras/camera-group?cameraGroupId=${data.cameraGroupId}`, {
      replace: true,
    });
    onClose();
  });

  const onSubmit = () => {
    addGroupMutation.mutate(payload, {
      onError: () => {
        toast('Failed to create camera group. Please try again.', {
          type: 'error',
        });
      },
    });
  };

  const hasMandatoryFields = payload.name && payload.locationId;

  const { data } = useLocations('');
  const locationOptions =
    data?.items.map((loc) => ({
      id: String(loc.id),
      name: loc.name,
    })) ?? [];

  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg min-h-screen">
      <CameraGroupHeader
        title="New Camera Group"
        subtitle="Select cameras from a location"
        actions={
          <>
            <Button
              variant="primary"
              onClick={onSubmit}
              loading={addGroupMutation.isLoading}
              disabled={!hasMandatoryFields || addGroupMutation.isLoading}
            >
              Save
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </>
        }
      />
      <div className="space-y-6 px-6 pt-4">
        <div
          className={
            payload.locationId
              ? 'grid grid-cols-1 gap-6 lg:grid-cols-2'
              : undefined
          }
        >
          <CameraGroupFormCard
            mode="create"
            name={payload.name}
            description={payload.description || ''}
            locationId={payload.locationId}
            locationOptions={locationOptions}
            onNameChange={(name) => setPayload({ ...payload, name })}
            onDescriptionChange={(description) =>
              setPayload({ ...payload, description })
            }
            onLocationChange={(locationId) =>
              setPayload({ ...payload, locationId })
            }
          />
          {payload.locationId && <Floorplan locationId={payload.locationId} />}
        </div>

        <CameraGroupSelectorCard
          locationId={payload.locationId}
          value={payload.cameras}
          onChange={(ids) => setPayload({ ...payload, cameras: ids })}
        />
      </div>
    </div>
  );
}

export default AddCameraGroup;
